import { catalogPage } from '@/shared/config/pageCatalog';
import { UNGATED } from '@/shared/config/sitemapCatalog';
import { useTranslation } from '@/shared/lib/common/i18n';
import type { ResolvedMenuNode } from '@/shared/ui/components/SideMenus/menuCatalog';
import { resolveMenuTree, visibleItemKeys } from '@/shared/ui/components/SideMenus/menuCatalog';
import useMenuContext from '@/shared/ui/components/SideMenus/useMenuContext';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Button } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PageLink } from '../lib/pageLinks';
import { useAiSearchStore } from '../model/aiSearchStore';

type Props = { link: PageLink | null };

const sidebarLabels = (nodes: ResolvedMenuNode[], labels = new Map<string, string>()) => {
  nodes.forEach(node => {
    node.items.forEach(item => item.key && !labels.has(item.key) && labels.set(item.key, item.label));
    sidebarLabels(node.children, labels);
  });
  return labels;
};

/** Opens the page with the full data, closing the panel that would cover it. */
const ViewOnPageLink = ({ link }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setOpen = useAiSearchStore(state => state.setOpen);
  const menuContext = useMenuContext();

  // The sidebar's own access rules and names, as the site map uses them.
  const { reachable, labels } = useMemo(
    () => ({ reachable: visibleItemKeys(menuContext), labels: sidebarLabels(resolveMenuTree(menuContext)) }),
    [menuContext]
  );

  const page = catalogPage(link?.path);
  const openable = page?.keys.filter(key => key === UNGATED || reachable.has(key)) ?? [];
  if (!link || !page || openable.length === 0) return null;

  const label = openable.map(key => labels.get(key)).find(Boolean) ?? page.label ?? page.path;

  return (
    // The main way on from an answer: the full data, on its own page.
    <Button
      variant="contained"
      color="primary"
      disableElevation
      endIcon={<ArrowForwardIcon />}
      onClick={() => {
        setOpen(false);
        navigate(link.to);
      }}
      sx={{
        alignSelf: 'flex-start',
        borderRadius: 999,
        px: 2.5,
        py: 0.75,
        textTransform: 'none',
        fontWeight: 600
      }}
    >
      {t.aiSearch.answer.seeAllOn(label)}
    </Button>
  );
};

export default ViewOnPageLink;
